import { Close as CloseIcon, ExpandMore } from '@mui/icons-material'
import {
  Drawer,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  IconButton,
  useTheme,
  AccordionDetails,
  alpha,
  Link as MuiLink,
} from '@mui/material'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'

import { guidanceSteps } from '~/features/onboarding/utils'
import useIsPartner from '~/features/partners/hooks/useIsPartner/v2'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import {
  SIDE_NAV_WIDTH,
  SIDE_NAV_WIDTH_COLLAPSED,
} from '~/layouts/account/header/nav/side/constants'
import { ETestId } from '~/models/tests'
import { store } from '~/store'
import rem from '~/utils/ui'

const AGENT_CHAT_URL = 'https://myreplify.ai/c/replify'

type TProps = {
  open: boolean
  onClose: () => void
  onMenuClose: () => void
}

function GuidanceDrawer({ open, onClose, onMenuClose }: TProps) {
  const theme = useTheme()
  const { partner } = usePartnerCentralized()
  const [expanded, setExpanded] = useState<string | false>(false)
  const isSideNavPinned = store((state) => state.UIElements.isSideNavPinned)
  const isPartner = useIsPartner()

  const contactEmail = !partner.supportEmail
    ? 'help@replify.ai'
    : partner.supportEmail

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false)
    }

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      variant="temporary"
      data-testid={ETestId.SetupGuidanceDrawer}
      sx={{
        zIndex: 3000,
      }}
      PaperProps={{
        sx: {
          width: { xxxs: '100%', md: rem(420) },
          left: {
            xxxs: 0,
            md: isSideNavPinned ? SIDE_NAV_WIDTH : SIDE_NAV_WIDTH_COLLAPSED,
          },
          bgcolor: theme.palette.background.paper,
          boxShadow: theme.shadows[3],
          borderLeft: 'none',
          top: 'unset',
          bottom: 0,

          height: {
            xxxs: '100%',
            md: `calc(100% - ${rem(56)})`,
          },
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            color: theme.palette.mainFont,
            boxShadow: `0 ${rem(2)} ${rem(5)} ${alpha(theme.palette.tertiary1, 0.1)}`,
            p: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              component="h2"
              fontWeight="bold"
              sx={{ fontSize: '1.1rem' }}
            >
              Setup Guidance
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.secondary1,
                mt: 0.5,
              }}
            >
              Follow these steps to optimize your assistant
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: theme.palette.secondary2 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {guidanceSteps.map((step, index) => (
            <Accordion
              key={step.id}
              expanded={expanded === step.id && step.details.length > 0}
              onChange={handleChange(step.id)}
              disableGutters
              elevation={0}
              sx={{
                bgcolor: theme.palette.background.paper,
                mb: 2,
                '&:before': { display: 'none' },
                boxShadow: `0 ${rem(1)} ${rem(4)} ${alpha(theme.palette.tertiary1, 0.12)}`,
                borderRadius: rem(8),
                '&.Mui-expanded': {
                  boxShadow: `0 ${rem(2)} ${rem(6)} ${alpha(theme.palette.tertiary1, 0.15)}`,
                  margin: `0 0 ${rem(16)} 0`,
                },
              }}
            >
              <AccordionSummary
                expandIcon={step.details ? <ExpandMore /> : null}
                sx={{
                  minHeight: 'auto',
                  py: 0.5,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'flex-start',
                    my: 1,
                    mx: 1,
                  },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {step?.link?.length ? (
                    <Link to={step.link} onClick={onMenuClose}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="medium"
                        sx={{
                          color: theme.palette.secondary1,
                          fontSize: rem(15),
                          lineHeight: rem(18),
                          '&:hover': {
                            textDecoration: 'underline',
                            color: theme.palette.tertiary1,
                          },
                        }}
                        onClick={onClose}
                      >
                        {index + 1}. {step.title}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography
                      variant="subtitle2"
                      fontWeight="medium"
                      sx={{
                        color: theme.palette.secondary1,
                        fontSize: rem(15),
                        lineHeight: rem(18),
                      }}
                    >
                      {index + 1}. {step.title}
                    </Typography>
                  )}

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 2, px: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {step.details}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        <Box
          sx={{
            bgcolor: theme.palette.background.paper,
            p: 2.5,
            textAlign: 'center',
            boxShadow: `0 ${rem(-2)} ${rem(5)} ${alpha(theme.palette.tertiary1, 0.08)}`,
            mt: 'auto',
            zIndex: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: theme.palette.mainFont,
              mb: 1,
              fontWeight: theme.typography.fontWeightBold,
            }}
          >
            Need help? Let us know!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isPartner ? (
              <>
                <Link to={`mailto:${contactEmail}`}>Contact us</Link> and we’ll
                provide any help you need!
              </>
            ) : (
              <>
                <MuiLink href={`mailto:${contactEmail}`}>Contact us</MuiLink> or{' '}
                <MuiLink
                  href={AGENT_CHAT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  chat
                </MuiLink>{' '}
                with our support agent.
              </>
            )}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  )
}

export default GuidanceDrawer
